import * as React from 'react';

import {
  DataGridColumn,
  LocalizedDate,
  maybeEmptyInfo,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $columnSelectedStatuses } from '../../../_state/containersList.state';
import { ContainerRecord, ContainerStatus } from '../../../types/container';
import { ContainerActionMenu } from '../ContainerActionMenu';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { ContainerStatusBadge } from '~/shared/components/ContainerStatusBadge';

export const useContainersListColumns = ({
  onRefetch,
  onVoid,
  onDoubleClick,
}: {
  onClick?: (row: ContainerRecord) => void;
  onDoubleClick?: (row: ContainerRecord) => void;
  onRefetch?: () => void;
  onVoid?: (containerId: string) => void;
}) => {
  const t = useTranslations();
  const columnSelectedStatuses = useAtomValue($columnSelectedStatuses);

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: ContainerRecord }) => {
      onDoubleClick?.(row);
    },
    [onDoubleClick],
  );

  const columns = React.useMemo(() => {
    const columnDefs: DataGridColumn<ContainerRecord>[] = [
      {
        key: 'containerNumber',
        header: t.containersView.table.columns.containerNumber,
        cell: ({ row }) => maybeEmptyInfo(row.containerNumber),
      },
      {
        key: 'booking',
        header: t.containersView.table.columns.booking,
        cell: ({ row }) => maybeEmptyInfo(row.booking?.bookingNumber),
      },
      {
        key: 'containerType',
        header: t.containersView.table.columns.containerType,
        cell: ({ row }) => maybeEmptyInfo(row.containerType?.name),
      },
      {
        key: 'facility',
        header: t.containersView.table.columns.facility,
        cell: ({ row }) => maybeEmptyInfo(row.facility?.facilityName),
      },
      {
        key: 'load',
        header: t.containersView.table.columns.load,
        cell: ({ row }) =>
          maybeEmptyInfo(row.load?.documentNumber, (v) => `#${v}`),
      },
      {
        key: 'status',
        header: t.containersView.table.columns.status,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.status, (v) => (
            <ContainerStatusBadge status={v as ContainerStatus} />
          )),
        isFilterActive: columnSelectedStatuses.size > 0,
      },
      {
        key: 'createdAt',
        header: t.containersView.table.columns.createdAt,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.timestamps?.createdAt, (v) => (
            <LocalizedDate date={v} />
          )),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => {
          const container = row as ContainerRecord;
          return (
            <ContainerActionMenu
              container={container}
              onVoid={onVoid}
              onRefetch={onRefetch}
            />
          );
        },
      },
    ];

    return columnDefs.map((it) => ({
      ...it,
      onDoubleClick: it.key === 'actions' ? undefined : handleCellDoubleClick,
    }));
  }, [t, onVoid, onRefetch, handleCellDoubleClick, columnSelectedStatuses]);

  return { columns };
};
